// Paladin's Hammer SLAM Animation Effect
// Adapted from AnimationStudio.tsx 3D SLAM animation to 2D canvas

// Configuration based on AnimationStudio.tsx SLAM effect
const CONFIG = {
    // Hammer
    hammerSize: 60,
    hammerColor: '#4a5568',
    handleColor: '#8B4513',
    goldAccent: '#b45309',

    // Flight
    flightDuration: 800, // ms
    arcHeight: 200, // pixels for arc trajectory

    // Impact
    debrisCount: 30,
    crackCount: 6,
    crackLength: 100,
    shakeIntensity: 15,
    shakeDuration: 400,

    // Trail
    trailParticleCount: 5,
    trailColor: { r: 74, g: 85, b: 104 }, // slate-600
}

// GLOBAL SPRITE CACHE FOR HAMMER SLAM EFFECT
const hammerSlamSpriteCache = new Map<string, HTMLCanvasElement>();

function getHammerTrailSprite(size: number): HTMLCanvasElement {
    const qSize = Math.max(1, Math.round(size));
    const key = `trail-${qSize}`;
    let sprite = hammerSlamSpriteCache.get(key);

    if (!sprite) {
        sprite = document.createElement('canvas');
        sprite.width = qSize * 2;
        sprite.height = qSize * 2;
        const sCtx = sprite.getContext('2d');
        if (sCtx) {
            const centerX = qSize;
            const centerY = qSize;
            const grad = sCtx.createRadialGradient(centerX, centerY, 0, centerX, centerY, qSize);
            grad.addColorStop(0, 'rgba(74, 85, 104, 0.8)');
            grad.addColorStop(1, 'rgba(74, 85, 104, 0)');
            sCtx.fillStyle = grad;
            sCtx.beginPath();
            sCtx.arc(centerX, centerY, qSize, 0, Math.PI * 2);
            sCtx.fill();
        }
        hammerSlamSpriteCache.set(key, sprite);
    }
    return sprite;
}

function getHammerImpactFlashSprite(): HTMLCanvasElement {
    const key = `impact-flash`;
    let sprite = hammerSlamSpriteCache.get(key);

    if (!sprite) {
        const qSize = 150;
        sprite = document.createElement('canvas');
        sprite.width = qSize * 2;
        sprite.height = qSize * 2;
        const sCtx = sprite.getContext('2d');
        if (sCtx) {
            const centerX = qSize;
            const centerY = qSize;
            const grad = sCtx.createRadialGradient(centerX, centerY, 0, centerX, centerY, qSize);
            grad.addColorStop(0, 'rgba(255, 255, 255, 1)');
            grad.addColorStop(0.3, 'rgba(251, 191, 36, 0.8)');
            grad.addColorStop(1, 'rgba(251, 191, 36, 0)');
            sCtx.fillStyle = grad;
            sCtx.beginPath();
            sCtx.arc(centerX, centerY, qSize, 0, Math.PI * 2);
            sCtx.fill();
        }
        hammerSlamSpriteCache.set(key, sprite);
    }
    return sprite;
}

type AnimationPhase = 'CHARGING' | 'FLYING' | 'IMPACT' | 'RECOVERY'

type DebrisParticle = {
    x: number
    y: number
    vx: number
    vy: number
    rot: number
    vRot: number
    size: number
    life: number
}

type CrackLine = {
    angle: number
    length: number
    segments: { x: number; y: number }[]
    opacity: number
}

type TrailParticle = {
    x: number
    y: number
    vx: number
    vy: number
    size: number
    life: number
    maxLife: number
}

export type HammerSlamData = {
    phase: AnimationPhase
    phaseTime: number
    pos: { x: number; y: number }
    rotation: number
    scale: { x: number; y: number }
    debris: DebrisParticle[]
    cracks: CrackLine[]
    trail: TrailParticle[]
    shakeOffset: { x: number; y: number }
}

export type HammerSlamEffect = {
    kind: 'hammerSlam'
    t: number
    sx: number
    sy: number
    tx: number
    ty: number
    data?: HammerSlamData
}

export const createHammerSlamEffect = (sx: number, sy: number, tx: number, ty: number): HammerSlamEffect => {
    return {
        kind: 'hammerSlam',
        t: 0,
        sx,
        sy,
        tx,
        ty
    }
}

// Generate procedural crack segments
const generateCrackSegments = (startX: number, startY: number, angle: number, length: number): { x: number; y: number }[] => {
    const segments: { x: number; y: number }[] = [{ x: startX, y: startY }]
    let currentDist = 0
    let currentAngle = angle
    let x = startX
    let y = startY

    while (currentDist < length) {
        const stepLength = 10 + Math.random() * 20
        currentDist += stepLength
        currentAngle += (Math.random() - 0.5) * 0.6 // Zigzag

        x += Math.cos(currentAngle) * stepLength
        y += Math.sin(currentAngle) * stepLength

        segments.push({ x, y })
    }

    return segments
}

// Draw the hammer procedurally
const drawHammer = (ctx: CanvasRenderingContext2D, rotation: number, scaleX: number, scaleY: number) => {
    ctx.save()
    ctx.rotate(rotation)
    ctx.scale(scaleX, scaleY)

    // Handle
    ctx.fillStyle = CONFIG.handleColor
    ctx.fillRect(-6, -60, 12, 80)

    // Handle wrapping (decorative)
    ctx.strokeStyle = CONFIG.goldAccent
    ctx.lineWidth = 2
    for (let i = 0; i < 5; i++) {
        ctx.beginPath()
        ctx.moveTo(-6, -50 + i * 15)
        ctx.lineTo(6, -50 + i * 15)
        ctx.stroke()
    }

    // Hammer head
    ctx.fillStyle = CONFIG.hammerColor
    ctx.beginPath()
    ctx.moveTo(-35, -70)
    ctx.lineTo(35, -70)
    ctx.lineTo(40, -50)
    ctx.lineTo(-40, -50)
    ctx.closePath()
    ctx.fill()

    // Hammer head highlight
    ctx.fillStyle = 'rgba(255, 255, 255, 0.2)'
    ctx.beginPath()
    ctx.moveTo(-30, -68)
    ctx.lineTo(30, -68)
    ctx.lineTo(32, -60)
    ctx.lineTo(-32, -60)
    ctx.closePath()
    ctx.fill()

    // Hammer head side accents (gold)
    ctx.fillStyle = CONFIG.goldAccent
    ctx.fillRect(-38, -70, 5, 20)
    ctx.fillRect(33, -70, 5, 20)

    // Glowing rune on hammer head
    ctx.shadowColor = '#fbbf24'
    ctx.shadowBlur = 10
    ctx.fillStyle = '#fbbf24'
    ctx.beginPath()
    ctx.arc(0, -60, 5, 0, Math.PI * 2)
    ctx.fill()
    ctx.shadowBlur = 0

    ctx.restore()
}

export const renderHammerSlamEffect = (
    eff: HammerSlamEffect,
    ctx: CanvasRenderingContext2D
): boolean => {
    // Initialize data if missing
    if (!eff.data) {
        eff.data = {
            phase: 'CHARGING',
            phaseTime: 0,
            pos: { x: eff.sx, y: eff.sy },
            rotation: 0,
            scale: { x: 1, y: 1 },
            debris: [],
            cracks: [],
            trail: [],
            shakeOffset: { x: 0, y: 0 }
        }
    }

    const d = eff.data
    d.phaseTime += 16.7 // ~60fps frame time

    // --- PHASE STATE MACHINE ---

    if (d.phase === 'CHARGING') {
        // Quick charge animation - hammer rises
        const chargeDuration = 300
        const t = Math.min(d.phaseTime / chargeDuration, 1)

        // Rise up and rotate back
        d.pos.y = eff.sy - t * 50
        d.rotation = t * -0.3

        // Slight squash-stretch anticipation
        d.scale.x = 1 + t * 0.1
        d.scale.y = 1 - t * 0.1

        if (d.phaseTime >= chargeDuration) {
            d.phase = 'FLYING'
            d.phaseTime = 0
        }
    } else if (d.phase === 'FLYING') {
        const t = Math.min(d.phaseTime / CONFIG.flightDuration, 1)

        // Arc trajectory using quadratic bezier
        const midX = (eff.sx + eff.tx) / 2
        const midY = Math.min(eff.sy, eff.ty) - CONFIG.arcHeight

        // Quadratic bezier: (1-t)^2 * P0 + 2(1-t)t * P1 + t^2 * P2
        const oneMinusT = 1 - t
        d.pos.x = oneMinusT * oneMinusT * eff.sx + 2 * oneMinusT * t * midX + t * t * eff.tx
        d.pos.y = oneMinusT * oneMinusT * eff.sy + 2 * oneMinusT * t * midY + t * t * eff.ty

        // Rotation during flight - spin as it arcs
        d.rotation = t * Math.PI * 2

        // Reset scale for flight
        d.scale.x = 1
        d.scale.y = 1

        // Emit trail particles
        if (Math.random() < 0.6) {
            for (let i = 0; i < CONFIG.trailParticleCount; i++) {
                d.trail.push({
                    x: d.pos.x + (Math.random() - 0.5) * 30,
                    y: d.pos.y + (Math.random() - 0.5) * 30,
                    vx: (Math.random() - 0.5) * 2,
                    vy: (Math.random() - 0.5) * 2 + 1, // Slight downward bias
                    size: 3 + Math.random() * 5,
                    life: 1,
                    maxLife: 30 + Math.random() * 20
                })
            }
        }

        if (t >= 1) {
            d.phase = 'IMPACT'
            d.phaseTime = 0

            // Spawn debris burst
            for (let i = 0; i < CONFIG.debrisCount; i++) {
                const angle = Math.random() * Math.PI * 2
                const speed = 5 + Math.random() * 15
                d.debris.push({
                    x: eff.tx,
                    y: eff.ty,
                    vx: Math.cos(angle) * speed,
                    vy: Math.sin(angle) * speed - Math.random() * 8, // Bias upward
                    rot: Math.random() * Math.PI * 2,
                    vRot: (Math.random() - 0.5) * 0.4,
                    size: 4 + Math.random() * 8,
                    life: 1
                })
            }

            // Generate cracks
            for (let i = 0; i < CONFIG.crackCount; i++) {
                const angle = (i / CONFIG.crackCount) * Math.PI * 2
                const length = CONFIG.crackLength * (0.6 + Math.random() * 0.4)
                d.cracks.push({
                    angle,
                    length,
                    segments: generateCrackSegments(eff.tx, eff.ty, angle, length),
                    opacity: 1
                })
            }

            // Impact squash
            d.scale.x = 1.5
            d.scale.y = 0.5
        }
    } else if (d.phase === 'IMPACT') {
        const impactDuration = 150
        const t = d.phaseTime / impactDuration

        // Camera shake
        if (d.phaseTime < CONFIG.shakeDuration) {
            const shakeDecay = 1 - d.phaseTime / CONFIG.shakeDuration
            d.shakeOffset.x = (Math.random() - 0.5) * CONFIG.shakeIntensity * shakeDecay
            d.shakeOffset.y = (Math.random() - 0.5) * CONFIG.shakeIntensity * shakeDecay
        } else {
            d.shakeOffset.x = 0
            d.shakeOffset.y = 0
        }

        // Recover from squash
        d.scale.x = 1.5 - Math.min(t, 1) * 0.5
        d.scale.y = 0.5 + Math.min(t, 1) * 0.5
        d.rotation = 0

        if (d.phaseTime >= impactDuration) {
            d.phase = 'RECOVERY'
            d.phaseTime = 0
        }
    } else if (d.phase === 'RECOVERY') {
        const recoveryDuration = 800
        const t = d.phaseTime / recoveryDuration

        // Fade cracks
        d.cracks.forEach(c => {
            c.opacity = Math.max(0, 1 - t)
        })

        // Shake fade
        d.shakeOffset.x = 0
        d.shakeOffset.y = 0

        if (t >= 1 && d.debris.length === 0 && d.trail.length === 0) {
            return false // Effect complete
        }
    }

    // --- UPDATE PARTICLES ---

    // Trail particles
    for (let i = d.trail.length - 1; i >= 0; i--) {
        const p = d.trail[i]
        p.x += p.vx
        p.y += p.vy
        p.life -= 1 / p.maxLife
        p.size *= 0.95
        if (p.life <= 0 || p.size <= 0.5) {
            d.trail[i] = d.trail[d.trail.length - 1]
            d.trail.pop()
        }
    }

    // Debris particles
    for (let i = d.debris.length - 1; i >= 0; i--) {
        const p = d.debris[i]
        p.x += p.vx
        p.y += p.vy
        p.vy += 0.5 // Gravity
        p.rot += p.vRot
        p.life -= 0.015

        // Floor collision
        if (p.y > eff.ty + 50) {
            p.y = eff.ty + 50
            p.vy *= -0.3
            p.vx *= 0.8
        }

        if (p.life <= 0) {
            d.debris[i] = d.debris[d.debris.length - 1]
            d.debris.pop()
        }
    }

    // --- RENDER ---

    ctx.save()

    // Apply screen shake
    ctx.translate(d.shakeOffset.x, d.shakeOffset.y)

    // 1. Draw Cracks (behind everything)
    d.cracks.forEach(crack => {
        if (crack.opacity <= 0) return

        ctx.save()
        ctx.globalAlpha = crack.opacity * 0.8
        ctx.strokeStyle = '#1a1a1a'
        ctx.lineWidth = 3
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'

        ctx.beginPath()
        crack.segments.forEach((seg, i) => {
            if (i === 0) ctx.moveTo(seg.x, seg.y)
            else ctx.lineTo(seg.x, seg.y)
        })
        ctx.stroke()

        // Inner lighter crack
        ctx.strokeStyle = '#3a3a3a'
        ctx.lineWidth = 1.5
        ctx.stroke()

        ctx.restore()
    })

    // 2. Draw Trail particles
    d.trail.forEach(p => {
        ctx.globalAlpha = p.life * 0.6
        const trailSprite = getHammerTrailSprite(p.size);
        ctx.drawImage(trailSprite, p.x - p.size, p.y - p.size);
    })

    // 3. Draw Debris
    d.debris.forEach(p => {
        ctx.save()
        ctx.globalAlpha = p.life
        ctx.translate(p.x, p.y)
        ctx.rotate(p.rot)

        // Rock/debris chunk
        ctx.fillStyle = '#555'
        ctx.beginPath()
        ctx.moveTo(-p.size / 2, -p.size / 3)
        ctx.lineTo(p.size / 2, -p.size / 2)
        ctx.lineTo(p.size / 3, p.size / 2)
        ctx.lineTo(-p.size / 3, p.size / 3)
        ctx.closePath()
        ctx.fill()

        ctx.restore()
    })

    // 4. Draw Hammer (if not in late recovery)
    if (d.phase !== 'RECOVERY' || d.phaseTime < 300) {
        const hammerOpacity = d.phase === 'RECOVERY' ? Math.max(0, 1 - d.phaseTime / 300) : 1

        ctx.save()
        ctx.globalAlpha = hammerOpacity
        ctx.translate(d.pos.x, d.pos.y)

        // Draw shadow
        ctx.save()
        ctx.scale(1, 0.3)
        ctx.beginPath()
        ctx.arc(0, 20, 30, 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(0, 0, 0, 0.3)'
        ctx.fill()
        ctx.restore()

        // Draw hammer
        drawHammer(ctx, d.rotation, d.scale.x, d.scale.y)

        ctx.restore()
    }

    // 5. Impact flash (only at impact moment)
    if (d.phase === 'IMPACT' && d.phaseTime < 100) {
        const flashOpacity = 1 - d.phaseTime / 100
        ctx.globalAlpha = flashOpacity * 0.5

        const flashSprite = getHammerImpactFlashSprite();
        ctx.drawImage(flashSprite, eff.tx - 150, eff.ty - 150);
    }

    ctx.restore()
    return true
}
